import * as React from 'react';

import {
  GetProductsByIdBatchedQuery,
  RoutineByCatalogIdQuery,
  ProductIdType,
} from '@notino/shared/definitions/types';

import ProductItem from '@components/Universals/ProductItem';
import { useProductsByIdsBatched } from '@containers/ProductDetailContainer/ProductDetail/ProductsSection/useProductsByIdsBatched';
import { useGetMasterAddToCart } from '@helpers/googleTagManager';

import { GTM_LIST_LABEL } from './constants';
import { RoutineHeader } from './RoutineHeader';
import {
  RoutineContentWrapper,
  RoutineProducts,
} from './styled/RoutineContent';
import { ProductWrapper } from './styled/RoutineProduct';
import { trackImpressionWithProductList } from './tracking';

export interface IRoutineContentProps {
  routine: RoutineByCatalogIdQuery['RoutineByCatalogId']['routine'][number];
}

const RoutineContent: React.FC<IRoutineContentProps> = ({
  routine: { products, ...restRoutine },
}) => {
  const productIds = products.map((product) => product.productId);

  const { data, loading } = useProductsByIdsBatched(productIds, {
    idType: ProductIdType.Catalog,
    onCompleted: ({ vpProductByIds }) => {
      if (vpProductByIds?.length) {
        trackImpressionWithProductList(vpProductByIds);
      }
    },
  });

  const trackAddToCart = useGetMasterAddToCart(GTM_LIST_LABEL);

  const onProductAdded = React.useCallback(
    async (product: GetProductsByIdBatchedQuery['vpProductByIds'][number]) => {
      await trackAddToCart(
        product,
        product.catalogMasterId.toString(),
        data.vpProductByIds.findIndex(
          ({ catalogMasterId }) =>
            Number(catalogMasterId) === Number(product.catalogMasterId)
        ) + 1
      );
    },
    [data, trackAddToCart]
  );

  const productsCount: number | undefined = data?.vpProductByIds?.length;
  if (loading || !productsCount) {
    return null;
  }

  return (
    <RoutineContentWrapper
      data-testid="routines"
      id="routines"
      productsCount={productsCount}
    >
      <RoutineHeader {...restRoutine} productsCount={productsCount} />
      <RoutineProducts productsCount={productsCount}>
        {data.vpProductByIds.map((product, index) => {
          if (!product) {
            return null;
          }

          const description = products[index]?.description;
          return (
            <ProductWrapper key={product.id} productsCount={productsCount}>
              <ProductItem
                buttonElementClassName="routine-buy-button"
                description={description}
                order={index + 1}
                position={index + 1}
                showBuyButton={true}
                GTMEventLabel={GTM_LIST_LABEL}
                sendGTM={true}
                onProductAdded={onProductAdded}
                noPriceIfUnavailable={true}
                product={product}
                inSectionOfType="routine"
                wishlistActionLocation="list"
              />
            </ProductWrapper>
          );
        })}
      </RoutineProducts>
    </RoutineContentWrapper>
  );
};

export default RoutineContent;
